/**
 * BubbleLevel.jsx
 * ---------------
 * A tiny, presentational spirit-level indicator.
 *
 * The bubble drifts inside its outer ring based on the offsets received
 * from the parent; parent decides how to compute those offsets from the
 * device tilt (kept out of this component on purpose so a future re-use
 * — e.g. for a horizon indicator — remains trivial).
 *
 * Props:
 *   level     — boolean, true when the phone is essentially flat
 *   offsetX   — px offset for the bubble on the horizontal axis
 *   offsetY   — px offset for the bubble on the vertical axis
 *   hint      — localized tooltip string
 */
import React from 'react';

export const BUBBLE_OUTER      = 42;   // px — outer level ring
export const BUBBLE_INNER      = 14;   // px — the bubble itself
export const BUBBLE_MAX_TILT   = 20;   // ±deg tilt that maps to the ring edge
export const BUBBLE_MAX_OFFSET = (BUBBLE_OUTER - BUBBLE_INNER) / 2 - 2;

export const BubbleLevel = ({ level, offsetX, offsetY, hint }) => (
  <div
    style={{
      width: BUBBLE_OUTER,
      height: BUBBLE_OUTER,
    }}
    data-testid="qibla-bubble-level"
    title={hint}
  >
    {/* Outer ring */}
    <div
      className="w-full h-full rounded-full flex items-center justify-center relative"
      style={{
        background: 'rgba(15, 23, 42, 0.55)',
        border: `1px solid ${level ? 'rgba(34,197,94,0.9)' : 'rgba(241,245,249,0.45)'}`,
        boxShadow: level
          ? '0 0 10px rgba(34,197,94,0.55), inset 0 0 6px rgba(34,197,94,0.25)'
          : 'inset 0 0 5px rgba(0,0,0,0.35)',
        transition: 'box-shadow 200ms ease-out, border-color 200ms ease-out',
      }}
    >
      {/* Crosshair — subtle */}
      <div className="absolute left-0 right-0 h-px" style={{ background: 'rgba(241,245,249,0.18)' }} />
      <div className="absolute top-0 bottom-0 w-px" style={{ background: 'rgba(241,245,249,0.18)' }} />

      {/* The bubble itself */}
      <div
        className="rounded-full"
        style={{
          width: BUBBLE_INNER,
          height: BUBBLE_INNER,
          background: level
            ? 'radial-gradient(circle at 30% 30%, rgba(134,239,172,0.95), rgba(34,197,94,0.85) 70%)'
            : 'radial-gradient(circle at 30% 30%, rgba(241,245,249,0.92), rgba(148,163,184,0.85) 70%)',
          boxShadow: level
            ? '0 0 6px rgba(34,197,94,0.7)'
            : '0 1px 2px rgba(0,0,0,0.4)',
          transform: `translate(${offsetX}px, ${offsetY}px)`,
          transition: 'background 180ms ease-out, box-shadow 180ms ease-out, transform 90ms linear',
        }}
      />
    </div>
  </div>
);
